import {matchCompanionProfile} from '../../utils/companionProfileMatch';
import './AdvanceFormVisibility.css';
import {readPriorCheckInAdvance} from '../../utils/priorCheckInAdvance';
import {runPmsTransaction as runTransaction} from "../../utils/pmsTransaction";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import GuestDetailsForm from "./GuestDetailsForm";
import GuestSearchInput from "./GuestSearchInput";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  findDuplicateGuestsInList,
  loadGuestProfiles,
  normalizeGuestIdNumber,
  normalizeGuestPhone,
  validateGuestProfile,
} from "../../services/guestProfileService";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeRoom = (value) =>
  clean(value).toUpperCase();

const getRoomCategory = (item) =>
  clean(
    typeof item === "object" && item
      ? item.roomCategory || item.category || item.roomType || ""
      : ""
  );

const toNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const nowForInput = () => {
  const date = new Date();

  date.setMinutes(
    date.getMinutes() -
      date.getTimezoneOffset()
  );

  return date
    .toISOString()
    .slice(0, 16);
};

const tomorrowForInput = () => {
  const date = new Date();

  date.setDate(date.getDate() + 1);
  date.setHours(11, 0, 0, 0);

  date.setMinutes(
    date.getMinutes() -
      date.getTimezoneOffset()
  );

  return date
    .toISOString()
    .slice(0, 16);
};

const createEmptyGuest = (
  isPrimary = false
) => ({
  profileId: "",
  searchTerm: "",

  name: "",
  phoneNumber: "",
  age: "",
  gender: "",
  nationality: "Indian",
  address: "",

  relation: isPrimary
    ? "Primary"
    : "",

  idType: "Aadhaar Card",
  idNumber: "",

  idFrontFile: null,
  idBackFile: null,

  idFrontUrl: "",
  idBackUrl: "",

  sameAddressAsPrimary: false,

  visitCount: 0,

  isVip: false,
  isBlacklisted: false,
  blacklistReason: "",

  isPrimary,
});

const getProfileId = (profile) =>
  clean(
    profile?.profileId ||
      profile?.id ||
      profile?.guestProfileId
  );

const getProfileGuest = (
  profile,
  existingGuest,
  index
) => ({
  ...existingGuest,

  profileId: getProfileId(profile),

  searchTerm:
    profile?.name ||
    profile?.phoneNumber ||
    profile?.phone ||
    profile?.idNumber ||
    "",

  name: clean(profile?.name),

  phoneNumber: normalizeGuestPhone(
    profile?.phoneNumber ||
      profile?.phone
  ),

  age:
    profile?.age === undefined ||
    profile?.age === null
      ? ""
      : String(profile.age),

  gender: clean(profile?.gender),

  nationality:
    clean(profile?.nationality) ||
    "Indian",

  address: clean(profile?.address),

  relation:
    index === 0
      ? "Primary"
      : clean(profile?.relation) ||
        clean(existingGuest?.relation),

  idType:
    clean(profile?.idType) ||
    "Aadhaar Card",

  idNumber:
    clean(profile?.idNumber),

  idFrontUrl:
    clean(profile?.idFrontUrl),

  idBackUrl:
    clean(profile?.idBackUrl),

  idFrontFile: null,
  idBackFile: null,

  visitCount: toNumber(
    profile?.visitCount
  ),

  isVip:
    profile?.isVip === true ||
    profile?.isVIP === true ||
    profile?.guestTag === "vip",

  isBlacklisted:
    profile?.isBlacklisted === true ||
    profile?.blacklisted === true ||
    profile?.guestTag ===
      "blacklist",

  blacklistReason: clean(
    profile?.blacklistReason
  ),

  isPrimary: index === 0,
});

const getGuestDisplayName = (
  guest,
  index
) =>
  clean(guest?.name) ||
  `Guest ${index + 1}`;

const formatMoney = (value) =>
  `₹${toNumber(value).toLocaleString(
    "en-IN",
    {
      maximumFractionDigits: 2,
    }
  )}`;

const escapeHtml = (value) =>
  clean(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getGuestIdPreviewUrl = (
  guest,
  side
) => {
  const file =
    side === "front"
      ? guest?.idFrontFile
      : guest?.idBackFile;

  const savedUrl =
    side === "front"
      ? guest?.idFrontUrl
      : guest?.idBackUrl;

  if (file) {
    try {
      return URL.createObjectURL(
        file
      );
    } catch {
      return "";
    }
  }

  return clean(savedUrl);
};

/* -------------------------------------------------------------------------- */
/*                              Reusable Field                                */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  required = false,
  error = "",
  children,
}) {
  return (
    <label style={fieldStyle}>
      <div style={labelStyle}>
        {label}

        {required && (
          <span style={requiredStyle}>
            {" "}
            *
          </span>
        )}
      </div>

      {children}

      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}
    </label>
  );
}

const DEFAULT_STAY_PLANS = [
  { name: "3 Hours", tariff: 1500 },
  { name: "6 Hours", tariff: "" },
  { name: "Same Day", tariff: 2000 },
  { name: "Night Stay", tariff: 2000 },
  { name: "Full Day", tariff: 3500 },
];

const normalizeStayPlans = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return DEFAULT_STAY_PLANS.map((plan) => ({ ...plan }));
  }

  const seen = new Set();
  const normalized = [];

  value.forEach((plan) => {
    const name = clean(plan?.name || plan?.title);
    if (!name || name.toLowerCase() === "custom" || seen.has(name.toLowerCase())) {
      return;
    }

    const rawTariff = plan?.tariff ?? plan?.price ?? "";
    const amount = rawTariff === "" ? "" : Number(rawTariff);

    if (amount !== "" && (!Number.isFinite(amount) || amount < 0)) {
      return;
    }

    seen.add(name.toLowerCase());
    normalized.push({ name, tariff: amount });
  });

  return normalized.length
    ? normalized
    : DEFAULT_STAY_PLANS.map((plan) => ({ ...plan }));
};

const legacyTariffsToPlans = (value = {}) => {
  if (!value || typeof value !== "object") {
    return DEFAULT_STAY_PLANS.map((plan) => ({ ...plan }));
  }

  const legacy = DEFAULT_STAY_PLANS.map((plan) => ({
    name: plan.name,
    tariff:
      value?.[plan.name] !== undefined && value?.[plan.name] !== null
        ? value[plan.name]
        : plan.tariff,
  }));

  return normalizeStayPlans(legacy);
};

const getStayPlanCacheKey = (hotelId) =>
  `hotelStayPlans_${clean(hotelId)}`;

/* -------------------------------------------------------------------------- */
/*                         Guest Check-In Modal                               */
/* -------------------------------------------------------------------------- */

function GuestCheckInModal({
  room,
  availableRooms = [],
  roomSelectionMode = false,
  hotelId,
  hotelName,

  saving = false,

  onClose,
  onSave,

  defaultTariff = "",
  defaultBookingSource = "Walk-in",
  initialProfile = null,
  reservation = null,
  initialGuestName = "",
  onQuickSaveName,
}) {

  const serialInputRef = useRef(null);
  const reservationLocalTime = value => {
    const date = new Date(value); date.setMinutes(date.getMinutes()-date.getTimezoneOffset());
    return date.toISOString().slice(0,16);
  };


  const [selectedRoom, setSelectedRoom] =
    useState(normalizeRoom(room));

  const roomOptions = useMemo(() => {
    const roomMap = new Map();

    (availableRooms || []).forEach((item) => {
      const optionRoom = normalizeRoom(
        typeof item === "string" ? item : item?.room
      );

      if (!optionRoom || roomMap.has(optionRoom)) return;

      roomMap.set(optionRoom, {
        room: optionRoom,
        category: getRoomCategory(item),
        isSir: item?.isSir === true,
      });
    });

    return [...roomMap.values()].sort((a, b) =>
      a.room.localeCompare(b.room, undefined, { numeric: true })
    );
  }, [availableRooms]);

  const effectiveRoom =
    normalizeRoom(room) ||
    normalizeRoom(selectedRoom);

  useEffect(() => {
    if (normalizeRoom(room)) {
      setSelectedRoom(normalizeRoom(room));
    }
  }, [room]);

  const [profiles, setProfiles] =
    useState([]);

  const [
    profilesLoading,
    setProfilesLoading,
  ] = useState(true);

  const [
    profilesError,
    setProfilesError,
  ] = useState("");

  const [
    companionSuggestions,
    setCompanionSuggestions,
  ] = useState([]);

  const [
    companionSuggestionsLoading,
    setCompanionSuggestionsLoading,
  ] = useState(false);

  const [
    companionSuggestionTargetIndex,
    setCompanionSuggestionTargetIndex,
  ] = useState(-1);

  const [guests, setGuests] = useState([
    {...createEmptyGuest(true),phoneNumber:reservation?.phoneNumber||''},
  ]);

  const [form, setForm] = useState({
    adRegisterSerialNo: "",

    children: "0",

    checkInAt: nowForInput(),
    expectedCheckOutAt: reservation ? reservationLocalTime(reservation.endAt) : tomorrowForInput(),

    tariff: reservation ? String(reservation.finalPrice) :
      defaultTariff === undefined ||
      defaultTariff === null ||
      defaultTariff === ""
        ? String(DEFAULT_STAY_PLANS.find((plan) => plan.name === "Full Day")?.tariff ?? "")
        : String(defaultTariff),

    // Payment split: OTA/FAB prepaid + guest se abhi Cash/Online received
    priorAdvanceAmount: "",
    priorAdvanceMode: "Cash",
    priorAdvanceReference: "",
    sourcePaidAmount: "",
    guestCashReceived: "",
    guestOnlineReceived: "",
    onlineTransactionReference: "",

    // Backward compatibility for old stay/payment fields.
    paymentMode: "Cash",
    transactionReference: "",

    extraPersonEnabled: false,
    extraPersonCount: "0",
    extraPersonRate: "",

    bookingSource:
      ["Walk-in", "Fab", "Brevistay", "OTA"].includes(
        defaultBookingSource
      )
        ? defaultBookingSource
        : "Walk-in",

    otaName: "",
    otaBookingId: "",

    corporateName: "",
    corporateReference: "",

    stayType: "Full Day",

    complimentary: reservation?.finalPrice === 0,

    earlyCheckIn: false,
    earlyCheckInCharge: "",

    lateCheckOut: false,
    lateCheckOutCharge: "",

    notes: "",
  });

  const [hotelStayPlans, setHotelStayPlans] =
    useState(() => normalizeStayPlans(DEFAULT_STAY_PLANS));

  const [tariffSettingsOpen, setTariffSettingsOpen] =
    useState(false);

  const [stayPlanDraft, setStayPlanDraft] =
    useState(() => normalizeStayPlans(DEFAULT_STAY_PLANS));

  const [tariffSettingsSaving, setTariffSettingsSaving] =
    useState(false);

  const [tariffSettingsNote, setTariffSettingsNote] =
    useState("");

  const [serialMode, setSerialMode] = useState('auto');
  const serialModeRef = useRef('auto');
  const [adRegisterId, setAdRegisterId] = useState('legacy');
  const [suggestedSerial, setSuggestedSerial] = useState('1');
  const [serialReuse, setSerialReuse] = useState(false);
  const [serialReuseReason, setSerialReuseReason] = useState('');
  const changeSerialMode = (mode) => {serialModeRef.current = mode; setSerialMode(mode);};

  const [
    formErrors,
    setFormErrors,
  ] = useState({});

  const [
    localSubmitting,
    setLocalSubmitting,
  ] = useState(false);

  const isSaving =
    saving || localSubmitting;

  /* ------------------------------------------------------------------------ */
  /*                         Initial Guest Prefill                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (initialProfile) {
      setGuests((current) => [
        getProfileGuest(initialProfile, current[0] || createEmptyGuest(true), 0),
        ...current.slice(1),
      ]);
      return;
    }

    const name = clean(initialGuestName);
    if (name) {
      setGuests((current) => [
        {
          ...(current[0] || createEmptyGuest(true)),
          name,
          searchTerm: name,
          isPrimary: true,
        },
        ...current.slice(1),
      ]);
    }
  }, [initialProfile, initialGuestName]);

  /* ------------------------------------------------------------------------ */
  /*                           Load Guest Profiles                             */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let active = true;

    if (!hotelId) {
      setProfiles([]);
      setProfilesLoading(false);
      setProfilesError(
        "Hotel ID missing hai."
      );

      return () => {
        active = false;
      };
    }

    setProfilesLoading(true);
    setProfilesError("");

    loadGuestProfiles({
      hotelId,
      includeBlacklisted: true,
    })
      .then((list) => {
        if (!active) return;

        setProfiles(
          Array.isArray(list)
            ? list
            : []
        );
      })
      .catch((error) => {
        console.error(
          "Guest profile load error:",
          error
        );

        if (!active) return;

        setProfiles([]);
        setProfilesError(
          error?.message ||
            "Guest profiles load nahi hui."
        );
      })
      .finally(() => {
        if (active) {
          setProfilesLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                  Previous Co-Guest / Companion History                   */
  /* ------------------------------------------------------------------------ */

  const primaryGuestProfileId =
    getProfileId(guests[0]);

  useEffect(() => {
    let active = true;

    if (
      !hotelId ||
      !primaryGuestProfileId
    ) {
      setCompanionSuggestions([]);
      setCompanionSuggestionsLoading(false);

      return () => {
        active = false;
      };
    }

    setCompanionSuggestionsLoading(true);

    const historyQuery = query(
      collection(db, "hotelStays"),
      where(
        "guestProfileIds",
        "array-contains",
        primaryGuestProfileId
      )
    );

    getDocs(historyQuery)
      .then((snapshot) => {
        if (!active) return;

        const grouped = new Map();

        snapshot.docs.forEach(
          (stayDocument) => {
            const stay =
              stayDocument.data() || {};

            if (
              clean(stay.hotelId) !==
              clean(hotelId)
            ) {
              return;
            }

            const stayGuests =
              Array.isArray(stay.guests)
                ? stay.guests
                : [];

            const rawDate =
              stay.checkInAt ||
              stay.occupiedAt ||
              stay.createdAt ||
              "";

            let stayedAt = 0;

            if (
              typeof rawDate?.toDate ===
              "function"
            ) {
              stayedAt =
                rawDate.toDate().getTime();
            } else if (rawDate?.seconds) {
              stayedAt =
                Number(rawDate.seconds) *
                1000;
            } else {
              const parsed =
                new Date(rawDate);

              stayedAt =
                Number.isNaN(
                  parsed.getTime()
                )
                  ? 0
                  : parsed.getTime();
            }

            stayGuests.forEach(
              (savedGuest) => {
                const savedProfileId =
                  getProfileId(
                    savedGuest
                  );

                if (
                  savedProfileId ===
                  primaryGuestProfileId
                ) {
                  return;
                }

                const savedId =
                  normalizeGuestIdNumber(
                    savedGuest?.idNumber
                  );

                const savedPhone =
                  normalizeGuestPhone(
                    savedGuest?.phoneNumber ||
                      savedGuest?.phone
                  );

                const savedName =
                  clean(
                    savedGuest?.name
                  );

                const identityKey =
                  savedProfileId
                    ? `profile:${savedProfileId}`
                    : savedId
                    ? `id:${savedId}`
                    : savedPhone
                    ? `phone:${savedPhone}:name:${savedName.toLowerCase()}`
                    : savedName
                    ? `name:${savedName.toLowerCase()}`
                    : "";

                if (!identityKey) {
                  return;
                }

                const existing =
                  grouped.get(
                    identityKey
                  ) || {
                    count: 0,
                    lastStayedAt: 0,
                    guest: savedGuest,
                  };

                grouped.set(
                  identityKey,
                  {
                    count:
                      existing.count + 1,
                    lastStayedAt:
                      Math.max(
                        existing.lastStayedAt,
                        stayedAt
                      ),
                    guest:
                      stayedAt >=
                      existing.lastStayedAt
                        ? savedGuest
                        : existing.guest,
                  }
                );
              }
            );
          }
        );

        const list = [
          ...grouped.entries(),
        ]
          .map(
            ([
              identityKey,
              history,
            ]) => {
              const savedGuest =
                history.guest || {};

              const savedProfileId =
                getProfileId(
                  savedGuest
                );

              const matchedProfile = matchCompanionProfile(savedGuest, profiles);

              return {
                key: identityKey,
                count:
                  history.count,
                lastStayedAt:
                  history.lastStayedAt,
                profile:
                  matchedProfile || {
                    ...savedGuest,
                    profileId:
                      savedProfileId,
                  },
              };
            }
          )
          .filter(
            (item) =>
              clean(
                item?.profile?.name
              )
          )
          .sort(
            (first, second) =>
              second.lastStayedAt -
                first.lastStayedAt ||
              second.count -
                first.count ||
              clean(
                first?.profile?.name
              ).localeCompare(
                clean(
                  second?.profile?.name
                )
              )
          )
          .slice(0, 8);

        setCompanionSuggestions(
          list
        );
      })
      .catch((error) => {
        console.error(
          "Previous co-guest history load error:",
          error
        );

        if (active) {
          setCompanionSuggestions(
            []
          );
        }
      })
      .finally(() => {
        if (active) {
          setCompanionSuggestionsLoading(
            false
          );
        }
      });

    return () => {
      active = false;
    };
  }, [
    hotelId,
    primaryGuestProfileId,
    profiles,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                         Hotel Stay Plans                                  */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let active = true;

    if (!hotelId) return () => { active = false; };

    const applyPlans = (plans) => {
      const normalized = normalizeStayPlans(plans);
      if (!active) return;

      setHotelStayPlans(normalized);
      setStayPlanDraft(normalized.map((plan) => ({ ...plan })));

      setForm((current) => {
        if (current.complimentary || reservation) return current;

        const selectedPlan = normalized.find(
          (plan) => plan.name === current.stayType
        );

        return {
          ...current,
          tariff:
            selectedPlan && selectedPlan.tariff !== ""
              ? String(selectedPlan.tariff)
              : current.tariff,
        };
      });
    };

    const loadStayPlans = async () => {
      let cached = null;

      try {
        const raw = localStorage.getItem(getStayPlanCacheKey(hotelId));
        if (raw) cached = normalizeStayPlans(JSON.parse(raw));
      } catch (error) {
        console.warn("Hotel stay plan local cache read error:", error);
      }

      if (cached) applyPlans(cached);

      try {
        const snapshot = await getDoc(doc(db, "hotelRoomBoards", hotelId));

        if (!active) return;

        if (!snapshot.exists()) {
          setSuggestedSerial('1');
          if (serialModeRef.current === 'auto') setForm(current => ({...current, adRegisterSerialNo: '1'}));
          return;
        }

        const data = snapshot.data() || {};
        setAdRegisterId(String(data.adRegisterId || 'legacy'));

        const nextSerial = Number(
          data.nextAdRegisterSerialNo ?? data.adRegisterNextSerialNo ?? (Number(data.lastAdRegisterSerialNo || 0) + 1)
        );

        if (Number.isFinite(nextSerial) && nextSerial > 0) {
          setSuggestedSerial(String(nextSerial));
          if (serialModeRef.current === 'auto') setForm((current) => ({
            ...current, adRegisterSerialNo: String(nextSerial),
          }));
        } else {
          setSuggestedSerial('1');
          if (serialModeRef.current === 'auto') setForm((current) => ({...current, adRegisterSerialNo: '1'}));
        }

        const cloudPlans = Array.isArray(data.stayPlans)
          ? data.stayPlans
          : legacyTariffsToPlans(data.tariffSettings);

        const normalized = normalizeStayPlans(cloudPlans);
        applyPlans(normalized);
        setTariffSettingsNote("");

        try {
          localStorage.setItem(
            getStayPlanCacheKey(hotelId),
            JSON.stringify(normalized)
          );
        } catch (error) {
          console.warn("Hotel stay plan local cache save error:", error);
        }
      } catch (error) {
        console.warn("Hotel stay plans load error:", error);
        if (active && !cached) {
          setTariffSettingsNote(
            "Saved hotel plans cloud se load nahi hue. Default plans dikh rahe hain."
          );
        }
      }
    };

    loadStayPlans();

    return () => { active = false; };
  }, [hotelId, reservation]);

  const saveHotelStayPlans = async () => {
    if (!hotelId || tariffSettingsSaving) return;

    const cleanedPlans = [];
    const seen = new Set();

    for (const row of stayPlanDraft) {
      const name = clean(row?.name);
      if (!name) {
        alert("Har plan ka naam required hai, jaise 3 Hours ya 5 Hours.");
        return;
      }

      if (name.toLowerCase() === "custom" || name.toLowerCase() === "edit plans") {
        alert(`Plan name "${name}" use nahi kar sakte.`);
        return;
      }

      if (seen.has(name.toLowerCase())) {
        alert(`${name} plan duplicate hai.`);
        return;
      }

      const rawTariff = row?.tariff;
      const amount = rawTariff === "" ? "" : Number(rawTariff);
      if (amount !== "" && (!Number.isFinite(amount) || amount < 0)) {
        alert(`${name} ka valid tariff enter karo.`);
        return;
      }

      seen.add(name.toLowerCase());
      cleanedPlans.push({ name, tariff: amount });
    }

    if (!cleanedPlans.length) {
      alert("Kam se kam 1 stay plan rakho.");
      return;
    }

    setTariffSettingsSaving(true);
    setTariffSettingsNote("");

    try {
      await runTransaction(db, async transaction => {
        const reference = doc(db, "hotelRoomBoards", hotelId);
        const snapshot = await transaction.get(reference);
        transaction.set(reference, {
          hotelId,
          stayPlans: cleanedPlans,
          stayPlansUpdatedAt: serverTimestamp(),
          schemaVersion: 2,
          revision: Number(snapshot.data()?.revision || 0) + 1,
        }, {merge: true});
      });

      try {
        localStorage.setItem(
          getStayPlanCacheKey(hotelId),
          JSON.stringify(cleanedPlans)
        );
      } catch (error) {
        console.warn("Hotel stay plan local cache save error:", error);
      }

      setHotelStayPlans(cleanedPlans);
      setStayPlanDraft(cleanedPlans.map((plan) => ({ ...plan })));

      setForm((current) => {
        if (current.complimentary || reservation) return current;
        const selectedPlan = cleanedPlans.find(
          (plan) => plan.name === current.stayType
        );

        return {
          ...current,
          stayType: selectedPlan ? current.stayType : "Custom",
          tariff:
            selectedPlan && selectedPlan.tariff !== ""
              ? String(selectedPlan.tariff)
              : current.tariff,
        };
      });

      setTariffSettingsNote(`${hotelName || hotelId} ke stay plans save ho gaye ✅`);
      setTimeout(() => setTariffSettingsOpen(false), 450);
    } catch (error) {
      console.error("Hotel stay plans save error:", error);
      setTariffSettingsNote(
        "Stay plans save nahi hue: " + (error?.message || error)
      );
    } finally {
      setTariffSettingsSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                              Calculations                                 */
  /* ------------------------------------------------------------------------ */

  const tariff = toNumber(
    form.tariff
  );

  const sourcePaidAmount = Math.max(
    0,
    toNumber(form.sourcePaidAmount)
  );

  const guestCashReceived = Math.max(
    0,
    toNumber(form.guestCashReceived)
  );

  const guestOnlineReceived = Math.max(
    0,
    toNumber(form.guestOnlineReceived)
  );

  const guestReceivedAmount =
    guestCashReceived +
    guestOnlineReceived;

  const extraPersonCount =
    form.extraPersonEnabled
      ? Math.max(
          0,
          Math.floor(
            toNumber(form.extraPersonCount)
          )
        )
      : 0;

  const extraPersonRate =
    form.extraPersonEnabled
      ? Math.max(
          0,
          toNumber(form.extraPersonRate)
        )
      : 0;

  const extraPersonCharge =
    extraPersonCount * extraPersonRate;

  // Backward-compatible total paid/advance used by existing billing flow.
  const advance =
    sourcePaidAmount +
    guestReceivedAmount + Number(reservation?.advanceAmount || 0) + Number(form.priorAdvanceAmount || 0);

  const earlyCheckInCharge =
    form.earlyCheckIn
      ? toNumber(
          form.earlyCheckInCharge
        )
      : 0;

  const lateCheckOutCharge =
    form.lateCheckOut
      ? toNumber(
          form.lateCheckOutCharge
        )
      : 0;

  const baseStayAmount =
    form.complimentary
      ? 0
      : tariff;

  const totalStayAmount =
    baseStayAmount +
    extraPersonCharge +
    earlyCheckInCharge +
    lateCheckOutCharge;

  const balance = Math.max(
    totalStayAmount - advance,
    0
  );

  const excessAdvance = Math.max(
    advance - totalStayAmount,
    0
  );

  const selectedBlacklistGuests =
    useMemo(
      () =>
        guests.filter(
          (guest) =>
            guest.isBlacklisted ===
            true
        ),
      [guests]
    );

  /* ------------------------------------------------------------------------ */
  /*                             Form Updater                                  */
  /* ------------------------------------------------------------------------ */

  const updateForm = (
    field,
    value
  ) => {
    if(reservation && ['tariff','stayType','complimentary','expectedCheckOutAt','bookingSource','sourcePaidAmount','sourcePaymentMode'].includes(field)) return;
    setForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (field === "stayType") {
        const selectedPlan = hotelStayPlans.find(
          (plan) => plan.name === value
        );

        if (!current.complimentary) {
          next.tariff =
            selectedPlan && selectedPlan.tariff !== ""
              ? String(selectedPlan.tariff)
              : "";
        }
      }

      if (
        field === "complimentary" &&
        value === true
      ) {
        next.tariff = "0";
      }

      if (
        field === "complimentary" &&
        value === false
      ) {
        const selectedPlan = hotelStayPlans.find(
          (plan) => plan.name === current.stayType
        );

        if (selectedPlan && selectedPlan.tariff !== "") {
          next.tariff = String(selectedPlan.tariff);
        }
      }

      if (
        field === "extraPersonEnabled" &&
        value === false
      ) {
        next.extraPersonCount = "0";
        next.extraPersonRate = "";
      }

      if (
        field === "extraPersonEnabled" &&
        value === true &&
        toNumber(next.extraPersonCount) <= 0
      ) {
        next.extraPersonCount = "1";
      }

      if (
        field === "paymentMode" &&
        value === "Cash"
      ) {
        next.transactionReference =
          "";
      }

      if (field === "bookingSource") {
        // Walk-in me source prepaid amount applicable nahi hai.
        if (value === "Walk-in") {
          next.sourcePaidAmount = "";
        }
      }

      if (
        field === "bookingSource"
      ) {
        next.otaName = "";
        next.otaBookingId = "";
        next.corporateName = "";
        next.corporateReference = "";
      }

      if (
        field === "earlyCheckIn" &&
        value === false
      ) {
        next.earlyCheckInCharge =
          "";
      }

      if (
        field === "lateCheckOut" &&
        value === false
      ) {
        next.lateCheckOutCharge =
          "";
      }

      return next;
    });

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = {
        ...current,
      };

      delete next[field];

      return next;
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                             Guest Updater                                 */
  /* ------------------------------------------------------------------------ */

  const updateGuest = (
    index,
    field,
    value
  ) => {
    setGuests((currentGuests) => {
      const nextGuests =
        currentGuests.map(
          (guest, guestIndex) => {
            if (
              guestIndex !== index
            ) {
              return guest;
            }

            return {
              ...guest,
              [field]: value,
            };
          }
        );

      const primaryAddress =
        nextGuests[0]?.address ||
        "";

      return nextGuests.map(
        (guest, guestIndex) => {
          if (
            guestIndex > 0 &&
            guest.sameAddressAsPrimary
          ) {
            return {
              ...guest,
              address:
                primaryAddress,
            };
          }

          return guest;
        }
      );
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                         Existing Profile Select                           */
  /* ------------------------------------------------------------------------ */

  const selectProfile = (
    index,
    profile
  ) => {
    const profileId =
      getProfileId(profile);

    const alreadySelectedIndex =
      guests.findIndex(
        (guest, guestIndex) =>
          guestIndex !== index &&
          getProfileId(guest) ===
            profileId
      );

    if (
      profileId &&
      alreadySelectedIndex >= 0
    ) {
      alert(
        `Ye guest pehle hi Guest ${
          alreadySelectedIndex + 1
        } me selected hai.`
      );

      return;
    }

    const selectedId =
      normalizeGuestIdNumber(
        profile?.idNumber
      );

    /*
     * Same phone number allowed hai (couple/family shared contact).
     * Same ID number ko hi duplicate guest maana jayega.
     */
    const duplicateIndex =
      guests.findIndex(
        (guest, guestIndex) => {
          if (guestIndex === index) {
            return false;
          }

          const guestId =
            normalizeGuestIdNumber(
              guest.idNumber
            );

          return Boolean(
            selectedId &&
            guestId === selectedId
          );
        }
      );

    if (duplicateIndex >= 0) {
      alert(
        `Same ID wala guest Guest ${
          duplicateIndex + 1
        } me already added hai.`
      );

      return;
    }

    setGuests((current) =>
      current.map(
        (guest, guestIndex) =>
          guestIndex === index
            ? getProfileGuest(
                profile,
                guest,
                index
              )
            : guest
      )
    );

    if (
      profile?.isBlacklisted ===
        true ||
      profile?.guestTag ===
        "blacklist"
    ) {
      alert(
        `Warning ⚠️\n\n${profile.name || "Guest"} blacklisted hai.\n\nReason: ${
          profile.blacklistReason ||
          "Reason available nahi hai."
        }`
      );
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                              Add Guest                                    */
  /* ------------------------------------------------------------------------ */

  const addGuest = () => {
    if (isSaving) return;

    if (guests.length >= 10) {
      alert(
        "Ek room check-in me maximum 10 guest add kar sakte hain."
      );

      return;
    }

    const nextGuestIndex =
      guests.length;

    setGuests((current) => [
      ...current,
      createEmptyGuest(false),
    ]);

    setCompanionSuggestionTargetIndex(
      primaryGuestProfileId
        ? nextGuestIndex
        : -1
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                             Remove Guest                                  */
  /* ------------------------------------------------------------------------ */

  const removeGuest = (index) => {
    if (isSaving || index === 0) {
      return;
    }

    setGuests((current) =>
      current.filter(
        (_, guestIndex) =>
          guestIndex !== index
      )
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                         Validate Complete Form                            */
  /* ------------------------------------------------------------------------ */

  const validateCompleteForm = () => {
    const errors = {};

    if (
      roomSelectionMode &&
      !effectiveRoom
    ) {
      errors.room =
        "Final step me Ready Room select karo.";
    } else if (
      roomSelectionMode &&
      effectiveRoom &&
      !roomOptions.some((item) => item.room === effectiveRoom)
    ) {
      errors.room =
        `Room ${effectiveRoom} ab Ready list me nahi hai. Dusra room select karo.`;
    }

    if (
      !clean(
        form.adRegisterSerialNo
      )
    ) {
      errors.adRegisterSerialNo =
        "A&D Register Serial Number required hai.";
    } else if (!/^[1-9]\d*$/.test(clean(form.adRegisterSerialNo)) || !Number.isSafeInteger(Number(form.adRegisterSerialNo))) {
      errors.adRegisterSerialNo = 'Positive whole serial number likho.';
    }
    if (serialMode === 'manual' && serialReuse && !serialReuseReason.trim())
      errors.adRegisterSerialNo = 'Same serial dobara use karne ka reason likho.';

    const checkInDate = new Date(
      form.checkInAt
    );

    const checkoutDate = new Date(
      form.expectedCheckOutAt
    );

    if (
      !form.checkInAt ||
      Number.isNaN(
        checkInDate.getTime()
      )
    ) {
      errors.checkInAt =
        "Valid check-in date/time required hai.";
    }

    if (
      !form.expectedCheckOutAt ||
      Number.isNaN(
        checkoutDate.getTime()
      )
    ) {
      errors.expectedCheckOutAt =
        "Valid expected checkout required hai.";
    }

    if (
      !errors.checkInAt &&
      !errors.expectedCheckOutAt &&
      checkoutDate <= checkInDate
    ) {
      errors.expectedCheckOutAt =
        "Expected checkout check-in ke baad hona chahiye.";
    }

    if (
      tariff < 0 ||
      advance < 0
    ) {
      errors.tariff =
        "Tariff aur advance negative nahi ho sakte.";
    }

    if (
      form.earlyCheckIn &&
      earlyCheckInCharge < 0
    ) {
      errors.earlyCheckInCharge =
        "Valid early check-in charge enter karo.";
    }

    if (
      form.lateCheckOut &&
      lateCheckOutCharge < 0
    ) {
      errors.lateCheckOutCharge =
        "Valid late checkout charge enter karo.";
    }

    if (
      guestOnlineReceived > 0 &&
      !clean(
        form.onlineTransactionReference
      )
    ) {
      errors.onlineTransactionReference =
        "Online payment reference required hai.";
    }

    if (
      form.extraPersonEnabled &&
      extraPersonCount > 0 &&
      extraPersonRate <= 0
    ) {
      errors.extraPersonRate =
        "Extra person rate enter karo.";
    }


    if (
      !Array.isArray(guests) ||
      guests.length === 0
    ) {
      errors.guests =
        "Kam se kam ek guest required hai.";
    }

    const duplicateIndexes =
      findDuplicateGuestsInList(
        guests
      );

    if (
      duplicateIndexes.length > 0
    ) {
      errors.guests =
        `Duplicate ID number Guest ${duplicateIndexes
          .map(
            (guestIndex) =>
              guestIndex + 1
          )
          .join(", ")} me mila.`;
    }

    const guestErrors = [];

    guests.forEach(
      (guest, index) => {
        const validation =
          validateGuestProfile(
            guest,
            {
              requirePhone:
                index === 0,
              requireId: true,
              requireAddress: true,
              requireAge: true,
            }
          );

        const errorsForGuest = {
          ...validation.errors,
        };

        if (
          !guest.idFrontFile &&
          !clean(
            guest.idFrontUrl
          )
        ) {
          errorsForGuest.idFrontFile =
            "ID Front photo required hai.";
        }

        if (
          Object.keys(
            errorsForGuest
          ).length > 0
        ) {
          guestErrors.push({
            index,
            name:
              getGuestDisplayName(
                guest,
                index
              ),
            errors:
              errorsForGuest,
          });
        }
      }
    );

    if (guestErrors.length > 0) {
      errors.guests =
        guestErrors
          .map(
            (guestError) => {
              const firstError =
                Object.values(
                  guestError.errors
                )[0];

              return `${guestError.name}: ${firstError}`;
            }
          )
          .join("\n");
    }

    setFormErrors(errors);

    return {
      valid:
        Object.keys(errors)
          .length === 0,

      errors,
      checkInDate,
      checkoutDate,
    };
  };

  const canSubmit = useMemo(() => {
    if (
      roomSelectionMode &&
      (
        !effectiveRoom ||
        !roomOptions.some((item) => item.room === effectiveRoom)
      )
    ) {
      return false;
    }

    if (
      !clean(
        form.adRegisterSerialNo
      )
    ) {
      return false;
    }

    if (
      !form.checkInAt ||
      !form.expectedCheckOutAt
    ) {
      return false;
    }

    if (
      guests.length === 0
    ) {
      return false;
    }

    const duplicateIndexes =
      findDuplicateGuestsInList(
        guests
      );

    if (
      duplicateIndexes.length > 0
    ) {
      return false;
    }

    return guests.every(
      (guest, index) => {
        const phone =
          normalizeGuestPhone(
            guest.phoneNumber
          );

        const validPhone =
          index === 0
            ? /^\d{10,15}$/.test(
                phone
              )
            : !phone ||
              /^\d{10,15}$/.test(
                phone
              );

        return Boolean(
          clean(guest.name) &&
            toNumber(
              guest.age
            ) > 0 &&
            clean(
              guest.nationality
            ) &&
            clean(
              guest.address
            ) &&
            clean(
              guest.idType
            ) &&
            clean(
              guest.idNumber
            ) &&
            validPhone &&
            (guest.idFrontFile ||
              clean(
                guest.idFrontUrl
              ))
        );
      }
    );
  }, [
    roomSelectionMode,
    effectiveRoom,
    roomOptions,
    form.adRegisterSerialNo,
    form.checkInAt,
    form.expectedCheckOutAt,
    guests,
  ]);

  const printGuestIdsBeforeCheckIn =
    () => {
      const printWindow =
        window.open(
          "",
          "_blank",
          "width=1050,height=820"
        );

      if (!printWindow) {
        alert(
          "Browser popup block kar raha hai. Popups allow karo."
        );
        return;
      }

      const temporaryUrls = [];

      const guestHtml =
        guests
          .map(
            (guest, index) => {
              const front =
                getGuestIdPreviewUrl(
                  guest,
                  "front"
                );

              const back =
                getGuestIdPreviewUrl(
                  guest,
                  "back"
                );

              if (
                guest?.idFrontFile &&
                front
              ) {
                temporaryUrls.push(
                  front
                );
              }

              if (
                guest?.idBackFile &&
                back
              ) {
                temporaryUrls.push(
                  back
                );
              }

              const imageHtml = (
                title,
                url
              ) => `
                <div class="id-box">
                  <div class="id-title">
                    ${title}
                  </div>

                  ${
                    url
                      ? `<img src="${escapeHtml(
                          url
                        )}" alt="${title}" />`
                      : `<div class="missing">No ID image</div>`
                  }
                </div>
              `;

              return `
                <section class="guest">
                  <div class="guest-head">
                    ${
                      index === 0
                        ? "Primary Guest"
                        : `Guest ${index + 1}`
                    }
                    •
                    ${escapeHtml(
                      guest?.name ||
                        "-"
                    )}
                  </div>

                  <div class="meta">
                    <span>
                      Phone:
                      ${escapeHtml(
                        guest?.phoneNumber ||
                          "-"
                      )}
                    </span>

                    <span>
                      ID:
                      ${escapeHtml(
                        guest?.idType ||
                          "-"
                      )}
                    </span>

                    <span>
                      ID No:
                      ${escapeHtml(
                        guest?.idNumber ||
                          "-"
                      )}
                    </span>
                  </div>

                  <div class="ids">
                    ${imageHtml(
                      "ID Front",
                      front
                    )}

                    ${imageHtml(
                      "ID Back",
                      back
                    )}
                  </div>

                  <div class="signature">
                    Guest Signature:
                    ______________________________
                  </div>
                </section>
              `;
            }
          )
          .join("");

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>
              Guest IDs - Room ${escapeHtml(
                room || ""
              )}
            </title>

            <style>
              * {
                box-sizing: border-box;
              }

              body {
                margin: 0;
                padding: 18px;
                font-family: Arial, sans-serif;
                color: #111827;
              }

              .header {
                text-align: center;
                margin-bottom: 14px;
              }

              .hotel {
                font-size: 22px;
                font-weight: 900;
              }

              .room {
                margin-top: 4px;
                font-size: 13px;
              }

              .guest {
                border: 1.5px solid #334155;
                padding: 12px;
                margin-bottom: 14px;
                break-inside: avoid;
              }

              .guest-head {
                font-size: 16px;
                font-weight: 900;
              }

              .meta {
                display: flex;
                gap: 18px;
                flex-wrap: wrap;
                margin-top: 7px;
                font-size: 11px;
              }

              .ids {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-top: 10px;
              }

              .id-box {
                border: 1px solid #94a3b8;
                min-height: 250px;
                padding: 8px;
              }

              .id-title {
                margin-bottom: 6px;
                font-size: 12px;
                font-weight: 900;
              }

              .id-box img {
                width: 100%;
                height: 215px;
                object-fit: contain;
                display: block;
              }

              .missing {
                height: 215px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #94a3b8;
              }

              .signature {
                margin-top: 25px;
                font-size: 11px;
                font-weight: 700;
              }

              @page {
                size: A4 portrait;
                margin: 10mm;
              }
            </style>
          </head>

          <body>
            <div class="header">
              <div class="hotel">
                ${escapeHtml(
                  hotelName ||
                    hotelId ||
                    "Hotel"
                )}
              </div>

              <div class="room">
                Guest ID Copy • Room
                ${escapeHtml(
                  room || "-"
                )}
                • A&D Serial
                ${escapeHtml(
                  form.adRegisterSerialNo ||
                    "-"
                )}
              </div>
            </div>

            ${guestHtml}

            <script>
              const images =
                Array.from(
                  document.images
                );

              Promise.all(
                images.map(
                  (img) =>
                    img.complete
                      ? Promise.resolve()
                      : new Promise(
                          (resolve) => {
                            img.onload = resolve;
                            img.onerror = resolve;
                          }
                        )
                )
              ).then(() => {
                setTimeout(
                  () => window.print(),
                  250
                );
              });
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();

      setTimeout(() => {
        temporaryUrls.forEach(
          (url) => {
            try {
              URL.revokeObjectURL(
                url
              );
            } catch {
              // ignore
            }
          }
        );
      }, 60000);
    };

  /* ------------------------------------------------------------------------ */
  /*                            Final Submit                                   */
  /* ------------------------------------------------------------------------ */

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (
      isSaving ||
      typeof onSave !== "function"
    ) {
      return;
    }

    try { readPriorCheckInAdvance(form, Number(reservation?.advanceAmount || 0), tariff); }
    catch(error) { alert(error.message); return; }
    const validation =
      validateCompleteForm();

    if (!validation.valid) {
      const firstError =
        Object.values(
          validation.errors
        )[0];

      alert(
        `Check-in form complete nahi hai.\n\n${
          firstError ||
          "Required details check karo."
        }`
      );

      return;
    }

    if (
      selectedBlacklistGuests.length >
      0
    ) {
      const names =
        selectedBlacklistGuests
          .map(
            (guest) =>
              guest.name ||
              "Guest"
          )
          .join(", ");

      const confirmed =
        window.confirm(
          `Warning ⚠️\n\nBlacklisted guest selected:\n${names}\n\nKya Manager verification ke baad check-in continue karna hai?`
        );

      if (!confirmed) return;
    }

    if (
      advance >
        totalStayAmount &&
      totalStayAmount >= 0
    ) {
      const confirmed =
        window.confirm(
          `Total received ${formatMoney(
            advance
          )} current room tariff/charges ${formatMoney(
            totalStayAmount
          )} se zyada hai.\n\nExtra advance: ${formatMoney(
            excessAdvance
          )}\nYe Advance & Pending Received section me carry hoga.\n\nKya check-in continue karna hai?`
        );

      if (!confirmed) return;
    }

    const cleanGuests =
      guests.map(
        (guest, index) => ({
          ...guest,

          profileId:
            getProfileId(guest),

          searchTerm: clean(
            guest.searchTerm
          ),

          name: clean(
            guest.name
          ),

          phoneNumber:
            normalizeGuestPhone(
              guest.phoneNumber
            ),

          age: toNumber(
            guest.age
          ),

          gender: clean(
            guest.gender
          ),

          nationality:
            clean(
              guest.nationality
            ) || "Indian",

          address: clean(
            guest.address
          ),

          relation:
            index === 0
              ? "Primary"
              : clean(
                  guest.relation
                ),

          idType: clean(
            guest.idType
          ),

          idNumber: clean(
            guest.idNumber
          ),

          isPrimary:
            index === 0,
        })
      );

    const primaryGuest =
      cleanGuests[0];

    const payload = {
      ...form,
      adRegisterSerialMode: serialMode,
      adRegisterId,
      adRegisterAllowReuse: serialMode === 'manual' && serialReuse,
      adRegisterReuseReason: serialMode === 'manual' && serialReuse ? serialReuseReason : '',

      room: effectiveRoom,

      adRegisterSerialNo:
        clean(
          form.adRegisterSerialNo
        ),

      adults:
        cleanGuests.length,

      children: Math.max(
        0,
        toNumber(
          form.children
        )
      ),

      checkInAt:
        form.checkInAt,

      expectedCheckOutAt:
        form.expectedCheckOutAt,

      tariff:
        form.complimentary
          ? 0
          : tariff,

      baseTariff:
        form.complimentary
          ? 0
          : tariff,

      earlyCheckInCharge,

      lateCheckOutCharge,

      totalStayAmount,

      advance,

      sourcePaidAmount,
      guestCashReceived,
      guestOnlineReceived,
      guestReceivedAmount,
      onlineTransactionReference:
        clean(
          form.onlineTransactionReference
        ),

      extraPersonEnabled:
        form.extraPersonEnabled === true,
      extraPersonCount,
      extraPersonRate,
      extraPersonCharge,

      sourcePaymentMode:
        form.bookingSource === "Fab"
          ? "Fab"
          : form.bookingSource === "Brevistay"
          ? "Brevistay"
          : form.bookingSource === "OTA"
          ? "OTA"
          : "",

      balance,

      excessAdvance,

      complimentary:
        Boolean(
          form.complimentary
        ),

      paymentMode:
        guestCashReceived > 0 &&
        guestOnlineReceived > 0
          ? "Mixed"
          : guestOnlineReceived > 0
          ? "UPI"
          : guestCashReceived > 0
          ? "Cash"
          : "Pending",

      transactionReference:
        guestOnlineReceived > 0
          ? clean(
              form.onlineTransactionReference
            )
          : "",

      bookingSource:
        clean(
          form.bookingSource
        ),

      otaName:
        clean(
          form.otaName
        ),

      otaBookingId:
        clean(
          form.otaBookingId
        ),

      corporateName:
        clean(
          form.corporateName
        ),

      corporateReference:
        clean(
          form.corporateReference
        ),

      stayType:
        clean(
          form.stayType
        ),

      notes: clean(
        form.notes
      ),

      guests: cleanGuests,

      guestName:
        primaryGuest.name,

      phoneNumber:
        primaryGuest.phoneNumber,

      age:
        primaryGuest.age,

      gender:
        primaryGuest.gender,

      nationality:
        primaryGuest.nationality,

      idType:
        primaryGuest.idType,

      idNumber:
        primaryGuest.idNumber,

      address:
        primaryGuest.address,

      primaryGuestProfileId:
        primaryGuest.profileId ||
        "",
    };

    setLocalSubmitting(true);

    try {
      await onSave(payload);
    } catch (error) {
      console.error(
        "Guest check-in submit error:",
        error
      );

      alert(
        "Check-in save nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setLocalSubmitting(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                            Keyboard Shortcuts                             */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const handleKeyboard = (
      event
    ) => {
      if (
        event.key ===
          "Escape" &&
        !isSaving
      ) {
        onClose?.();
      }

      if (
        (event.ctrlKey ||
          event.metaKey) &&
        event.key === "Enter"
      ) {
        event.preventDefault();

        document
          .getElementById(
            "guest-check-in-form"
          )
          ?.requestSubmit();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyboard
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyboard
      );
  }, [isSaving, onClose]);

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */



  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
            event.currentTarget &&
          !isSaving
        ) {
          onClose?.();
        }
      }}
    >
      <form
        id="guest-check-in-form"
        className="advance-readable"
        onSubmit={handleSubmit}
        style={modalStyle}
      >
        <div style={headerStyle}>
          <div>
            <h2 style={titleStyle}>
              Guest Check-In
            </h2>
            {reservation && <p style={{background:'#edf6fa',padding:12,borderRadius:8}}>
              Reserved booking · Room {reservation.room} · Total ₹{reservation.finalPrice}<br />
              Advance already received ₹{Number(reservation.advanceAmount||0)} · Remaining ₹{Math.max(0,Number(reservation.finalPrice)-Number(reservation.advanceAmount||0))}<br />
              Neeche sirf abhi mil raha naya Cash / Online amount bharein.<br />
              Departure: {new Date(reservation.endAt).toLocaleString('en-IN',{timeZone:'Asia/Kolkata'})} (IST).
            </p>}


            <div style={subtitleStyle}>
              {hotelName ||
                "Hotel"}{" "}
              • {effectiveRoom
                ? `Room ${effectiveRoom}`
                : "Room select at final step"}
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              onClose?.()
            }
            disabled={isSaving}
            style={closeButtonStyle}
          >
            ×
          </button>
        </div>

        {profilesError && (
          <div style={warningBoxStyle}>
            Guest profiles load nahi
            hui: {profilesError}
          </div>
        )}

        <section style={{ ...sectionStyle, borderColor: "#bfdbfe", background: "#f8fbff" }}>
          <div style={sectionHeadingStyle}>Search Existing Guest</div>
          <div style={sectionHelpStyle}>
            Returning guest ko sabse pehle yahan search karo. New guest ho to naam type karke Full Check-In ya Name Only use kar sakte ho.
          </div>

          <div style={{ marginTop: 10 }}>
            <GuestSearchInput
              profiles={profiles}
              value={guests[0]?.searchTerm || guests[0]?.name || ""}
              disabled={isSaving}
              loading={profilesLoading}
              hotelId={hotelId}
              placeholder="Name, phone ya ID number search karo"
              onChange={(value) => {
                updateGuest(0, "searchTerm", value);
                if (!guests[0]?.profileId) updateGuest(0, "name", value);
              }}
              onSelect={(profile) => selectProfile(0, profile)}
              onNewGuestAction={
                typeof onQuickSaveName === "function" &&
                !roomSelectionMode
                  ? async (quickName) => {
                      const name = clean(quickName);
                      if (!name) return;

                      const confirmed = window.confirm(
                        `${name} ko Room ${effectiveRoom} me Name Only ke saath occupy karna hai?\n\nBaad me Add Details se full check-in complete kar sakte ho.`
                      );

                      if (!confirmed) return;
                      await onQuickSaveName(name, effectiveRoom);
                    }
                  : undefined
              }
              newGuestActionLabel="⚡ Save Name Only & Occupy Room"
            />
          </div>

          {guests[0]?.profileId && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#166534", fontWeight: 800 }}>
              ✓ Returning guest selected: {guests[0]?.name || "Guest"}
            </div>
          )}

        </section>

        <section style={sectionStyle}>
          <div style={sectionHeadingStyle}>
            Registration Details
          </div>

          <div style={topGridStyle}>
            <Field
              label="A&D Register Serial No."
              required
              error={formErrors.adRegisterSerialNo}
            >
              <div style={{ display: "flex", gap: 7, alignItems: "stretch" }}>
                <input
                  ref={serialInputRef}
                  inputMode="numeric"
                  value={form.adRegisterSerialNo}
                  disabled={isSaving}
                  onChange={(event) => {if (serialMode !== 'new_register') changeSerialMode('manual'); updateForm("adRegisterSerialNo", event.target.value);}}
                  style={{
                    ...inputStyle,
                    flex: 1,
                    background: "#ffffff",
                    ...getErrorInputStyle(formErrors.adRegisterSerialNo),
                  }}
                  placeholder="Set starting serial"
                />

                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => {changeSerialMode('auto'); updateForm('adRegisterSerialNo', suggestedSerial);}}
                  title="Automatic numbering use karo"
                  style={{ minWidth: 56, border: "1px solid #cbd5e1", borderRadius: 10, background: "#ffffff", color: "#1d4ed8", fontWeight: 900, cursor: "pointer" }}
                >
                  Auto
                </button>
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: "#64748b" }}>
                {serialMode === 'auto' ? 'Automatic serial save ke waqt latest numbering se milega.' : serialMode === 'new_register' ? 'Naya register: is serial se numbering shuru hogi.' : 'Manual serial exactly save hoga. Chhota serial next numbering ko peeche nahi karega.'}
              </div>
              <label style={{display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginTop: 8}}>
                <input type="checkbox" disabled={isSaving} checked={serialMode === 'new_register'}
                  onChange={event => {changeSerialMode(event.target.checked ? 'new_register' : 'auto'); updateForm('adRegisterSerialNo', event.target.checked ? '1' : suggestedSerial);}} />
                Naya A&D register — numbering dobara shuru karein
              </label>
              {serialMode === 'manual' && <>
                <label style={{display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginTop: 8}}>
                  <input type="checkbox" disabled={isSaving} checked={serialReuse} onChange={event => setSerialReuse(event.target.checked)} />
                  Same serial dobara use karein (entry correction)
                </label>
                {serialReuse && <input value={serialReuseReason} disabled={isSaving} maxLength={200}
                  onChange={event => setSerialReuseReason(event.target.value)}
                  placeholder="Reason: pehle galat entry mein number lag gaya tha"
                  aria-label="Same A&D serial reuse reason" style={{...inputStyle, marginTop: 6}} />}
              </>}
            </Field>

            <Field label="Total Adults">
              <input
                value={guests.length}
                disabled
                style={{
                  ...inputStyle,
                  background: "#f1f5f9",
                }}
              />
            </Field>

            <Field label="Children">
              <input
                type="number"
                min="0"
                value={form.children}
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "children",
                    event.target.value
                  )
                }
                style={inputStyle}
              />
            </Field>

            <Field label="Stay Type">
              <select
                value={form.stayType}
                disabled={isSaving}
                onChange={(event) => {
                  const value = event.target.value;

                  if (value === "__EDIT_PLANS__") {
                    setStayPlanDraft(
                      hotelStayPlans.map((plan) => ({ ...plan }))
                    );
                    setTariffSettingsNote("");
                    setTariffSettingsOpen(true);
                    return;
                  }

                  updateForm("stayType", value);
                }}
                style={inputStyle}
              >
                {hotelStayPlans.map((plan) => (
                  <option key={plan.name} value={plan.name}>
                    {plan.name}
                    {plan.tariff !== "" ? ` — ₹${plan.tariff}` : ""}
                  </option>
                ))}
                <option value="Custom">Custom</option>
                <option value="__EDIT_PLANS__">✏ Edit Plans / Tariffs</option>
              </select>
            </Field>
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeadingStyle}>
            Guest Details
          </div>

          <div style={sectionHelpStyle}>
            {profilesLoading
              ? "Old guest profiles loading..."
              : "Primary guest fill/select karo. Extra guest isi primary guest box ke niche add kar sakte ho."}
          </div>

          {formErrors.guests && (
            <div style={errorBoxStyle}>
              {formErrors.guests}
            </div>
          )}

          {guests.map(
            (guest, index) => (
              <React.Fragment
                key={`${index}-${
                  guest.profileId ||
                  "new"
                }`}
              >
                {index > 0 &&
                  index ===
                    companionSuggestionTargetIndex &&
                  primaryGuestProfileId && (
                    <div
                      style={{
                        marginTop: 12,
                        marginBottom: 10,
                        padding: 12,
                        borderRadius: 14,
                        border:
                          "1px solid #dbeafe",
                        background:
                          "#f8fbff",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 950,
                          color: "#1e3a8a",
                        }}
                      >
                        Previous Guests with{" "}
                        {guests[0]?.name ||
                          "Primary Guest"}
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 11,
                          color: "#64748b",
                        }}
                      >
                        Pehle saath stay kar chuke guest ko select karo, ya niche new guest manually add karo.
                      </div>

                      {companionSuggestionsLoading ? (
                        <div
                          style={{
                            marginTop: 9,
                            fontSize: 11,
                            color: "#64748b",
                          }}
                        >
                          Previous guest suggestions loading...
                        </div>
                      ) : companionSuggestions.length >
                        0 ? (
                        <div
                          style={{
                            marginTop: 10,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 7,
                          }}
                        >
                          {companionSuggestions.map(
                            (suggestion) => (
                              <button
                                key={
                                  suggestion.key
                                }
                                type="button"
                                disabled={
                                  isSaving
                                }
                                onClick={() => {
                                  selectProfile(
                                    index,
                                    suggestion.profile
                                  );
                                  setCompanionSuggestionTargetIndex(
                                    -1
                                  );
                                }}
                                style={{
                                  border:
                                    "1px solid #bfdbfe",
                                  borderRadius:
                                    999,
                                  background:
                                    "#ffffff",
                                  color:
                                    "#1e40af",
                                  padding:
                                    "7px 10px",
                                  fontSize: 11,
                                  fontWeight:
                                    850,
                                  cursor:
                                    "pointer",
                                }}
                              >
                                {
                                  suggestion
                                    .profile
                                    ?.name
                                }
                                {suggestion.count >
                                1
                                  ? ` • ${suggestion.count} stays`
                                  : ""}
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            marginTop: 9,
                            fontSize: 11,
                            color: "#64748b",
                          }}
                        >
                          Is guest ke saath pehle koi saved co-guest nahi mila. New guest niche add karo.
                        </div>
                      )}
                    </div>
                  )}

                <GuestDetailsForm
                  guest={guest}
                  index={index}
                  hideSearch={index === 0}
                  profiles={profiles}
                  profilesLoading={
                    profilesLoading
                  }
                  disabled={isSaving}
                  primaryAddress={
                    guests[0]?.address ||
                    ""
                  }
                  onChange={
                    updateGuest
                  }
                  onSelectProfile={
                    selectProfile
                  }
                  onRemove={
                    removeGuest
                  }
                />

                {index === 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "flex-start",
                      margin:
                        "10px 0 4px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={addGuest}
                      disabled={
                        isSaving
                      }
                      style={
                        addGuestButtonStyle
                      }
                    >
                      + Add Another Guest
                    </button>
                  </div>
                )}
              </React.Fragment>
            )
          )}
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeadingStyle}>
            Stay & Payment Details
          </div>

          <div style={topGridStyle}>
            <Field
              label="Check-In Date & Time"
              required
              error={
                formErrors.checkInAt
              }
            >
              <input
                type="datetime-local"
                value={form.checkInAt}
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "checkInAt",
                    event.target.value
                  )
                }
                style={{
                  ...inputStyle,
                  ...getErrorInputStyle(
                    formErrors.checkInAt
                  ),
                }}
              />
            </Field>

            <Field
              label="Expected Checkout"
              required
              error={
                formErrors.expectedCheckOutAt
              }
            >
              <input
                type="datetime-local"
                value={
                  form.expectedCheckOutAt
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "expectedCheckOutAt",
                    event.target.value
                  )
                }
                style={{
                  ...inputStyle,
                  ...getErrorInputStyle(
                    formErrors.expectedCheckOutAt
                  ),
                }}
              />
            </Field>

            <Field
              label="Room Tariff"
              error={
                formErrors.tariff
              }
            >
              <input
                type="number"
                min="0"
                value={form.tariff}
                disabled={
                  !!reservation || isSaving ||
                  form.complimentary
                }
                onChange={(event) =>
                  updateForm(
                    "tariff",
                    event.target.value
                  )
                }
                style={{
                  ...inputStyle,
                  background:
                    form.complimentary
                      ? "#f1f5f9"
                      : "#ffffff",
                  ...getErrorInputStyle(
                    formErrors.tariff
                  ),
                }}
                placeholder="₹"
              />
            </Field>

            {form.bookingSource !== "Walk-in" && (
              <Field
                label={`${
                  form.bookingSource === "Fab"
                    ? "FAB"
                    : form.bookingSource === "Brevistay"
                    ? "Brevistay"
                    : "OTA"
                } Paid Amount`}
              >
                <input
                  type="number"
                  min="0"
                  value={form.sourcePaidAmount}
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "sourcePaidAmount",
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="Already paid to OTA / source"
                />
              </Field>
            )}

            <Field label="Advance Already Received">
              <input type="number" min="0" step="0.01" value={form.priorAdvanceAmount} disabled={isSaving}
                onChange={e=>updateForm('priorAdvanceAmount',e.target.value)} style={inputStyle} placeholder="Pehle mila advance, e.g. 500" />
            </Field>
            {Number(form.priorAdvanceAmount)>0 && <>
              <Field label="Advance Payment Mode"><select style={inputStyle} value={form.priorAdvanceMode} disabled={isSaving}
                onChange={e=>updateForm('priorAdvanceMode',e.target.value)}><option value="Cash">Cash</option><option value="UPI">Online / UPI</option></select></Field>
              {form.priorAdvanceMode==='UPI' && <Field label="Advance Transaction Reference" required>
                <input style={inputStyle} disabled={isSaving} value={form.priorAdvanceReference}
                  onChange={e=>updateForm('priorAdvanceReference',e.target.value)} placeholder="Pehle wale advance ki ID, e.g. 1234" />
              </Field>}
              <div style={{gridColumn:'1 / -1',color:'#172b40',background:'#eff6ff',padding:12,borderRadius:10}}>
                Pehle mila advance yahan bharein. Ab mil raha payment neeche Cash / Online Received mein bharein.
                Software mein pehle se recorded advance dobara na bharein. Confirm Check-In se guest abhi check-in hoga.
              </div>
            </>}
            <Field label="Cash Received">
              <input
                type="number"
                min="0"
                value={form.guestCashReceived}
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "guestCashReceived",
                    event.target.value
                  )
                }
                style={inputStyle}
                placeholder="Cash amount"
              />
            </Field>

            <Field label="Online Received">
              <input
                type="number"
                min="0"
                value={form.guestOnlineReceived}
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "guestOnlineReceived",
                    event.target.value
                  )
                }
                style={inputStyle}
                placeholder="UPI / Card / Bank amount"
              />
            </Field>

            {guestOnlineReceived > 0 && (
              <Field
                label="Online Transaction Reference"
                required
                error={
                  formErrors.onlineTransactionReference
                }
              >
                <input
                  value={
                    form.onlineTransactionReference
                  }
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "onlineTransactionReference",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorInputStyle(
                      formErrors.onlineTransactionReference
                    ),
                  }}
                  placeholder="UPI / Card / Bank transaction ID"
                />
              </Field>
            )}

            <Field label="Booking Source">
              <select
                value={
                  form.bookingSource
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "bookingSource",
                    event.target.value
                  )
                }
                style={inputStyle}
              >
                <option value="Walk-in">
                  Walk-in
                </option>
                <option value="Fab">
                  Fab
                </option>
                <option value="Brevistay">
                  Brevistay
                </option>
                <option value="OTA">
                  Other OTA
                </option>
              </select>
            </Field>
          </div>

          <div style={paymentSplitSummaryStyle}>
            <div><b>Total Stay:</b> {formatMoney(totalStayAmount)}</div>
            <div><b>Booking advance already received:</b> {formatMoney(Number(reservation?.advanceAmount || 0))}</div>
            <div><b>Previously received advance (first entry):</b> {formatMoney(Number(form.priorAdvanceAmount || 0))}</div>
            <div><b>Source Paid:</b> {formatMoney(sourcePaidAmount)}</div>
            <div><b>Cash:</b> {formatMoney(guestCashReceived)}</div>
            <div><b>Online:</b> {formatMoney(guestOnlineReceived)}</div>
            <div><b>Total Paid:</b> {formatMoney(advance)}</div>
            <div><b>Balance:</b> {formatMoney(balance)}</div>
          </div>

          <div style={optionsGridStyle}>
            <label style={optionCardStyle}>
              <input
                type="checkbox"
                checked={
                  form.complimentary
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "complimentary",
                    event.target.checked
                  )
                }
              />

              <span>
                Complimentary Stay
              </span>
            </label>

            <label style={optionCardStyle}>
              <input
                type="checkbox"
                checked={
                  form.extraPersonEnabled
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "extraPersonEnabled",
                    event.target.checked
                  )
                }
              />

              <span>
                Extra Person
              </span>
            </label>

            <label style={optionCardStyle}>
              <input
                type="checkbox"
                checked={
                  form.earlyCheckIn
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "earlyCheckIn",
                    event.target.checked
                  )
                }
              />

              <span>
                Early Check-In
              </span>
            </label>

            <label style={optionCardStyle}>
              <input
                type="checkbox"
                checked={
                  form.lateCheckOut
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "lateCheckOut",
                    event.target.checked
                  )
                }
              />

              <span>
                Late Checkout
              </span>
            </label>
          </div>

          {form.extraPersonEnabled && (
            <div style={subGridStyle}>
              <Field label="Extra Persons">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.extraPersonCount}
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "extraPersonCount",
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="1"
                />
              </Field>

              <Field
                label="Extra Person Rate"
                error={formErrors.extraPersonRate}
              >
                <input
                  type="number"
                  min="0"
                  value={form.extraPersonRate}
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "extraPersonRate",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorInputStyle(
                      formErrors.extraPersonRate
                    ),
                  }}
                  placeholder="₹ per person"
                />
              </Field>

              <div style={summaryCardStyle}>
                <div style={summaryLabelStyle}>
                  Extra Person Charge
                </div>
                <div style={summaryValueStyle}>
                  {formatMoney(extraPersonCharge)}
                </div>
              </div>
            </div>
          )}

          {(form.earlyCheckIn ||
            form.lateCheckOut) && (
            <div style={subGridStyle}>
              {form.earlyCheckIn && (
                <Field
                  label="Early Check-In Charge"
                  error={
                    formErrors.earlyCheckInCharge
                  }
                >
                  <input
                    type="number"
                    min="0"
                    value={
                      form.earlyCheckInCharge
                    }
                    disabled={isSaving}
                    onChange={(event) =>
                      updateForm(
                        "earlyCheckInCharge",
                        event.target.value
                      )
                    }
                    style={{
                      ...inputStyle,
                      ...getErrorInputStyle(
                        formErrors.earlyCheckInCharge
                      ),
                    }}
                    placeholder="₹"
                  />
                </Field>
              )}

              {form.lateCheckOut && (
                <Field
                  label="Late Checkout Charge"
                  error={
                    formErrors.lateCheckOutCharge
                  }
                >
                  <input
                    type="number"
                    min="0"
                    value={
                      form.lateCheckOutCharge
                    }
                    disabled={isSaving}
                    onChange={(event) =>
                      updateForm(
                        "lateCheckOutCharge",
                        event.target.value
                      )
                    }
                    style={{
                      ...inputStyle,
                      ...getErrorInputStyle(
                        formErrors.lateCheckOutCharge
                      ),
                    }}
                    placeholder="₹"
                  />
                </Field>
              )}
            </div>
          )}

          <Field label="Notes">
            <textarea
              value={form.notes}
              disabled={isSaving}
              onChange={(event) =>
                updateForm(
                  "notes",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                minHeight: 80,
                resize: "vertical",
                marginTop: 6,
              }}
              placeholder="Guest request, room preference, payment note, special instruction..."
            />
          </Field>
        </section>

        {roomSelectionMode && (
          <section
            style={{
              ...sectionStyle,
              borderColor: "#86efac",
              background: "#f0fdf4",
            }}
          >
            <div style={sectionHeadingStyle}>
              Final Step — Select Room
            </div>
            <div style={sectionHelpStyle}>
              Guest select / details fill karne ke baad yahin Ready Room choose karke finish karo.
            </div>

            <select
              value={selectedRoom}
              disabled={isSaving}
              onChange={(event) =>
                setSelectedRoom(normalizeRoom(event.target.value))
              }
              style={{
                ...inputStyle,
                marginTop: 10,
                ...getErrorInputStyle(formErrors.room),
              }}
            >
              <option value="">Select Ready Room</option>
              {roomOptions.map((item) => (
                <option key={item.room} value={item.room}>
                  Room {item.room}
                  {item.category ? ` • ${item.category}` : ""}
                  {item.isSir ? " • SIR" : ""}
                </option>
              ))}
            </select>

            {formErrors.room && (
              <div style={errorStyle}>{formErrors.room}</div>
            )}

            {!guests[0]?.profileId &&
              clean(guests[0]?.name || guests[0]?.searchTerm) &&
              typeof onQuickSaveName === "function" && (
                <button
                  type="button"
                  disabled={isSaving || !effectiveRoom}
                  onClick={async () => {
                    const name = clean(
                      guests[0]?.name || guests[0]?.searchTerm
                    );

                    if (!name) {
                      return alert("Guest ka naam enter karo.");
                    }

                    if (!effectiveRoom) {
                      return alert("Pehle Ready Room select karo.");
                    }

                    const confirmed = window.confirm(
                      `${name} ko Room ${effectiveRoom} me Name Only ke saath occupy karna hai?\n\nBaad me Add Details se full guest details complete kar sakte ho.`
                    );

                    if (!confirmed) return;
                    await onQuickSaveName(name, effectiveRoom);
                  }}
                  style={{
                    ...primaryButtonStyle,
                    width: "100%",
                    marginTop: 10,
                    background: "#d97706",
                    opacity: isSaving || !effectiveRoom ? 0.55 : 1,
                    cursor: isSaving || !effectiveRoom ? "not-allowed" : "pointer",
                  }}
                >
                  ⚡ Save Name Only & Occupy Room
                </button>
              )}
          </section>
        )}

        <section style={summaryStyle}>
          <Summary
            label="Adults"
            value={guests.length}
          />

          <Summary
            label="Children"
            value={toNumber(
              form.children
            )}
          />

          <Summary
            label="Room Tariff"
            value={formatMoney(
              baseStayAmount
            )}
          />

          <Summary
            label="Extra Timing"
            value={formatMoney(
              earlyCheckInCharge +
                lateCheckOutCharge
            )}
          />

          <Summary
            label="Total"
            value={formatMoney(
              totalStayAmount
            )}
          />

          <Summary
            label="Advance"
            value={formatMoney(
              advance
            )}
          />

          <Summary
            label="Balance"
            value={formatMoney(
              balance
            )}
            danger={balance > 0}
          />

          {excessAdvance > 0 && (
            <Summary
              label="Excess Advance"
              value={formatMoney(
                excessAdvance
              )}
              warning
            />
          )}
        </section>

        {tariffSettingsOpen && (
          <div style={tariffSettingsOverlayStyle}>
            <div style={tariffSettingsModalStyle}>
              <div style={tariffSettingsHeaderStyle}>
                <div>
                  <div style={tariffSettingsTitleStyle}>
                    Hotel Stay Plans & Tariffs
                  </div>
                  <div style={tariffSettingsSubTitleStyle}>
                    {hotelName || hotelId || "Hotel"} • Ye rates sirf is hotel ke liye save honge.
                  </div>
                </div>

                <button
                  type="button"
                  disabled={tariffSettingsSaving}
                  onClick={() =>
                    setTariffSettingsOpen(false)
                  }
                  style={tariffSettingsCloseStyle}
                >
                  ×
                </button>
              </div>

              <div style={tariffSettingsHelpStyle}>
                Apne hotel ke plans khud banao — jaise 3 Hours, 5 Hours, Same Day, Night Stay. Plan select karte hi saved tariff auto-fill hoga.
              </div>

              <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
                {stayPlanDraft.map((plan, index) => (
                  <div
                    key={`${index}-${plan.name}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(130px, .65fr) auto",
                      gap: 8,
                      alignItems: "end",
                    }}
                  >
                    <Field label="Plan Name">
                      <input
                        value={plan.name}
                        disabled={tariffSettingsSaving}
                        onChange={(event) =>
                          setStayPlanDraft((current) =>
                            current.map((row, rowIndex) =>
                              rowIndex === index
                                ? { ...row, name: event.target.value }
                                : row
                            )
                          )
                        }
                        style={inputStyle}
                        placeholder="5 Hours / Night Stay"
                      />
                    </Field>

                    <Field label="Tariff">
                      <input
                        type="number"
                        min="0"
                        value={plan.tariff ?? ""}
                        disabled={tariffSettingsSaving}
                        onChange={(event) =>
                          setStayPlanDraft((current) =>
                            current.map((row, rowIndex) =>
                              rowIndex === index
                                ? { ...row, tariff: event.target.value }
                                : row
                            )
                          )
                        }
                        style={inputStyle}
                        placeholder="₹ amount"
                      />
                    </Field>

                    <button
                      type="button"
                      disabled={tariffSettingsSaving || stayPlanDraft.length <= 1}
                      onClick={() =>
                        setStayPlanDraft((current) =>
                          current.filter((_, rowIndex) => rowIndex !== index)
                        )
                      }
                      style={{
                        height: 46,
                        minWidth: 46,
                        border: "1px solid #fecaca",
                        borderRadius: 10,
                        background: "#fff1f2",
                        color: "#be123c",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      title="Remove plan"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                disabled={tariffSettingsSaving}
                onClick={() =>
                  setStayPlanDraft((current) => [
                    ...current,
                    { name: "", tariff: "" },
                  ])
                }
                style={{
                  width: "100%",
                  marginTop: 10,
                  padding: 10,
                  border: "1px dashed #60a5fa",
                  borderRadius: 10,
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                ＋ Add New Plan
              </button>

              {tariffSettingsNote && (
                <div style={tariffSettingsNoteStyle}>
                  {tariffSettingsNote}
                </div>
              )}

              <div style={tariffSettingsActionsStyle}>
                <button
                  type="button"
                  disabled={tariffSettingsSaving}
                  onClick={() =>
                    setTariffSettingsOpen(false)
                  }
                  style={tariffCancelButtonStyle}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={tariffSettingsSaving}
                  onClick={async () => {
                    await saveHotelStayPlans();
                  }}
                  style={tariffSaveButtonStyle}
                >
                  {tariffSettingsSaving
                    ? "Saving..."
                    : "Save Plans & Tariffs"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={footerStyle}>
          <div style={shortcutStyle}>
            Ctrl + Enter: Check-In • Esc:
            Close
          </div>

          <div style={footerButtonsStyle}>
            <button
              type="button"
              onClick={
                printGuestIdsBeforeCheckIn
              }
              disabled={isSaving}
              style={printIdsButtonStyle}
            >
              🪪 Print Guest IDs
            </button>

            <button
              type="button"
              onClick={() =>
                onClose?.()
              }
              disabled={isSaving}
              style={secondaryButtonStyle}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                isSaving ||
                !canSubmit
              }
              style={{
                ...primaryButtonStyle,
                opacity:
                  isSaving ||
                  !canSubmit
                    ? 0.55
                    : 1,
                cursor:
                  isSaving ||
                  !canSubmit
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isSaving
                ? "Profiles, IDs & Check-In Saving..."
                : "Confirm Check-In ✅"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Small Components                              */
/* -------------------------------------------------------------------------- */

function Summary({
  label,
  value,
  danger = false,
  warning = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...summaryValueStyle,
          color: danger
            ? "#dc2626"
            : warning
            ? "#c2410c"
            : "#0f172a",
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const getErrorInputStyle = (
  error
) =>
  error
    ? {
        borderColor: "#dc2626",
        boxShadow:
          "0 0 0 2px rgba(220,38,38,.08)",
      }
    : {};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,

  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.76)",
};

const modalStyle = {
  color: "#0f172a",
  colorScheme: "light",
  width: "min(1120px,100%)",
  maxHeight: "96vh",

  padding: 20,

  overflowY: "auto",

  borderRadius: 21,

  background: "#ffffff",

  boxSizing: "border-box",

  boxShadow:
    "0 28px 85px rgba(0,0,0,.32)",
};

const headerStyle = {
  position: "sticky",
  top: -20,
  zIndex: 20,

  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  gap: 15,

  margin: "-20px -20px 18px",
  padding: "17px 20px",

  background:
    "rgba(255,255,255,.97)",

  borderBottom:
    "1px solid #e2e8f0",

  backdropFilter: "blur(12px)",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,
  color: "#64748b",
  fontWeight: 700,
};

const closeButtonStyle = {
  width: 39,
  height: 39,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",
  color: "#334155",

  fontSize: 27,
  lineHeight: 1,

  cursor: "pointer",
};

const warningBoxStyle = {
  marginBottom: 13,
  padding: 11,

  borderRadius: 11,

  border:
    "1px solid #fed7aa",

  background: "#fff7ed",
  color: "#c2410c",

  fontSize: 12,
  fontWeight: 700,
};

const errorBoxStyle = {
  marginBottom: 13,
  padding: 11,

  whiteSpace: "pre-line",

  borderRadius: 11,

  border:
    "1px solid #fecaca",

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 12,
  fontWeight: 700,
};

const sectionStyle = {
  marginTop: 15,
  padding: 15,

  borderRadius: 16,

  border:
    "1px solid #e2e8f0",

  background: "#ffffff",
};

const sectionHeadingStyle = {
  color: "#0f172a",
  fontSize: 17,
  fontWeight: 900,
};

const sectionHelpStyle = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 11,
};

const topGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(210px,1fr))",

  gap: 12,

  marginTop: 13,
};

const subGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(240px,1fr))",

  gap: 12,

  marginTop: 13,
};

const guestSectionHeaderStyle = {
  display: "flex",

  justifyContent:
    "space-between",

  alignItems: "center",

  gap: 12,

  flexWrap: "wrap",

  marginBottom: 13,
};

const addGuestButtonStyle = {
  border: 0,

  borderRadius: 10,

  padding: "10px 13px",

  background: "#2563eb",
  color: "#ffffff",

  fontWeight: 900,

  cursor: "pointer",
};

const fieldStyle = {
  display: "block",
  width: "100%",
};

const labelStyle = {
  marginBottom: 6,

  color: "#334155",

  fontSize: 13,
  fontWeight: 800,
};

const requiredStyle = {
  color: "#dc2626",
};

const errorStyle = {
  marginTop: 5,

  color: "#dc2626",

  fontSize: 11,
  fontWeight: 700,
};

const inputStyle = {
  width: "100%",
  minHeight: 44,

  padding: "10px 11px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,

  background: "#ffffff",
  color: "#0f172a",

  boxSizing: "border-box",

  fontSize: 14,

  outline: "none",
};

const tariffSettingsButtonStyle = {
  width: "100%",
  marginTop: 7,
  minHeight: 34,
  border: "1px solid #93c5fd",
  borderRadius: 9,
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 11,
  fontWeight: 900,
  cursor: "pointer",
};

const tariffSettingsOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  background: "rgba(15,23,42,.58)",
};

const tariffSettingsModalStyle = {
  width: "min(720px,100%)",
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#ffffff",
  boxShadow: "0 24px 80px rgba(15,23,42,.35)",
  padding: 18,
};

const tariffSettingsHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
};

const tariffSettingsTitleStyle = {
  color: "#0f172a",
  fontSize: 20,
  fontWeight: 950,
};

const tariffSettingsSubTitleStyle = {
  marginTop: 4,
  color: "#64748b",
  fontSize: 12,
};

const tariffSettingsCloseStyle = {
  width: 38,
  height: 38,
  border: 0,
  borderRadius: 10,
  background: "#f1f5f9",
  color: "#334155",
  fontSize: 24,
  cursor: "pointer",
};

const tariffSettingsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
  gap: 12,
  marginTop: 18,
};

const tariffSettingsHelpStyle = {
  marginTop: 12,
  padding: 10,
  borderRadius: 10,
  background: "#f8fafc",
  color: "#475569",
  fontSize: 12,
};

const tariffSettingsNoteStyle = {
  marginTop: 10,
  color: "#166534",
  fontSize: 12,
  fontWeight: 800,
};

const tariffSettingsActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 16,
};

const tariffCancelButtonStyle = {
  minHeight: 40,
  padding: "0 16px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#ffffff",
  color: "#334155",
  fontWeight: 800,
  cursor: "pointer",
};

const tariffSaveButtonStyle = {
  minHeight: 40,
  padding: "0 16px",
  border: 0,
  borderRadius: 10,
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const optionsGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(180px,1fr))",

  gap: 10,

  marginTop: 14,
};

const optionCardStyle = {
  display: "flex",

  alignItems: "center",

  gap: 9,

  padding: 11,

  borderRadius: 11,

  border:
    "1px solid #cbd5e1",

  background: "#f8fafc",

  color: "#334155",

  fontSize: 12,
  fontWeight: 800,

  cursor: "pointer",
};

const summaryStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(125px,1fr))",

  gap: 10,

  marginTop: 16,
};

const summaryCardStyle = {
  padding: 12,

  borderRadius: 12,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",

  textAlign: "center",
};

const summaryLabelStyle = {
  color: "#64748b",

  fontSize: 10,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  marginTop: 5,

  fontSize: 17,
  fontWeight: 900,
};

const printIdsButtonStyle = {
  border: 0,
  borderRadius: 10,
  padding: "11px 15px",
  background: "#2563eb",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const footerStyle = {
  position: "sticky",
  bottom: -20,
  zIndex: 20,

  display: "flex",

  justifyContent:
    "space-between",

  alignItems: "center",

  gap: 12,

  flexWrap: "wrap",

  margin: "18px -20px -20px",
  padding: "14px 20px",

  background:
    "rgba(255,255,255,.98)",

  borderTop:
    "1px solid #e2e8f0",

  backdropFilter: "blur(12px)",
};

const shortcutStyle = {
  color: "#64748b",
  fontSize: 10,
};

const footerButtonsStyle = {
  display: "flex",

  justifyContent: "flex-end",

  gap: 10,

  flexWrap: "wrap",
};

const secondaryButtonStyle = {
  padding: "11px 16px",

  borderRadius: 10,

  border:
    "1px solid #cbd5e1",

  background: "#ffffff",
  color: "#334155",

  fontWeight: 800,

  cursor: "pointer",
};

const primaryButtonStyle = {
  padding: "11px 18px",

  border: 0,
  borderRadius: 10,

  background: "#16a34a",
  color: "#ffffff",

  fontWeight: 900,
};


const paymentSplitSummaryStyle = {
  marginTop: 12,
  padding: "10px 12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
  gap: 8,
  background: "#f8fafc",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  fontSize: 12,
  color: "#334155",
};

export default GuestCheckInModal;
